'use client';

/**
 * Circular swatches for one option (variant_swatches in CMS; usually color).
 * CMS: section_title is the Shopify option name; variants[] rows hold label + hex per swatch.
 */

import { useMemo } from 'react';
import { useProduct } from '@/contexts/ProductContext';
import { getSelectedOptionValue } from '@/utils/product/product-context-helpers';

import { getShopifyOptionByName } from '@/components/blocks/variantOptions/shopifyOptionConventions';

const VariantSwatches = ({ block, resolvedOptionName }) => {
  const { selectedVariant, updateOptions, productData } = useProduct();

  const { variants: swatchRows = [] } = block ?? {};

  const colorOption = getShopifyOptionByName(productData?.shopifyProduct, resolvedOptionName);

  const currentValue = getSelectedOptionValue(selectedVariant, resolvedOptionName);

  // Skip CMS rows for values Shopify no longer has (e.g. a removed color).
  const renderedSwatches = useMemo(() => {
    if (!colorOption) return [];
    return swatchRows.reduce((acc, swatch) => {
      const optionValue = colorOption.values.find(
        (v) => v.toLowerCase() === swatch.label?.toLowerCase(),
      );
      if (!optionValue) return acc;
      acc.push({ swatch, optionValue });
      return acc;
    }, []);
  }, [colorOption, swatchRows]);

  if (!renderedSwatches.length || !selectedVariant) return null;

  return (
    <section className="flex flex-col gap-4 font-worksans text-primary" data-block="variant_swatches">
      <div className="flex justify-between">
        <p className="text-base font-medium leading-5">
          {resolvedOptionName}
          {currentValue ? (
            <span className="font-normal"> {currentValue}</span>
          ) : null}
        </p>
      </div>

      <fieldset className="border-0 p-0">
        <legend className="sr-only">{resolvedOptionName}</legend>
        <div className="grid w-fit grid-cols-3 justify-items-start gap-x-2.5 gap-y-6">
          {renderedSwatches.map(({ swatch, optionValue }) => {
            const isSelected = currentValue === optionValue;

            return (
              <div
                key={optionValue}
                className="relative flex flex-col items-center"
              >
                <label
                  title={optionValue}
                  className={`relative mb-4 inline-block h-12 w-12 cursor-pointer rounded-full transition-colors duration-150 ${
                    isSelected
                      ? 'border-[2.5px] border-sea'
                      : 'border-2 border-sand-light hover:border-sand'
                  }`}
                  style={{ backgroundColor: swatch.hex_code }}
                >
                  <input
                    type="radio"
                    name={`variant-${resolvedOptionName}`}
                    value={optionValue}
                    checked={isSelected}
                    aria-label={optionValue}
                    className="sr-only"
                    onChange={() =>
                      updateOptions([{ name: resolvedOptionName, value: optionValue }], { persist: true })
                    }
                  />
                </label>
                <span className="font-bold text-center max-w-12 truncate">
                  {swatch.label}
                </span>
              </div>
            );
          })}
        </div>
      </fieldset>
    </section>
  );
};

export default VariantSwatches;
